import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { Thumb } from "@/components/tss/CommitteeCard";
import { PageHeader, Panel } from "@/pages/admin/shared";
import { CommitteeDialog } from "@/pages/admin/CommitteeDialog";
import { BulkUpload } from "@/pages/admin/BulkUpload";
import { PosterButton } from "@/pages/admin/PosterButton";

const Row = ({ c, onEdit, onDelete, onToggle }) => (
  <li data-testid={`admin-committee-row-${c.id}`} className="flex items-center gap-3 border-b border-gold/20 py-3 last:border-0">
    <Thumb committee={c} size="h-10 w-10" />
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold">{c.name}</p>
      <p className="truncate text-xs text-ink-muted">{c.area || "—"} · {c.votes} votes</p>
    </div>
    <Switch checked={c.active} onCheckedChange={onToggle} data-testid={`admin-committee-active-${c.id}`} />
    <PosterButton committee={c} />
    <button type="button" onClick={onEdit} data-testid={`admin-committee-edit-${c.id}`} className="rounded-lg p-2 text-maroon hover:bg-gold/20" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
    <button type="button" onClick={onDelete} data-testid={`admin-committee-delete-${c.id}`} className="rounded-lg p-2 text-maroon hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
  </li>
);

export default function Committees() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => api.get("/admin/committees").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? list.filter((c) => `${c.name} ${c.area}`.toLowerCase().includes(s)) : list;
  }, [q, list]);

  const toggle = async (c, active) => {
    try { await api.put(`/admin/committees/${c.id}`, { active }); toast.success(active ? "Activated" : "Hidden from public site"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/committees/${toDelete.id}`); toast.success("Committee deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <div data-testid="admin-committees">
      <PageHeader title="Committees" sub={`${list.filter((c) => c.active).length} active of ${list.length}`}>
        <button type="button" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-committee-btn" className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-semibold text-white">
          <Plus className="h-4 w-4" /> Add committee
        </button>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Panel>
          <label className="relative mb-2 block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search committees…" data-testid="admin-committee-search" className="h-10 w-full rounded-full border border-gold/40 bg-ivory pl-9 pr-3 text-sm outline-none focus:border-gold" />
          </label>
          <ul>
            {filtered.map((c) => (
              <Row key={c.id} c={c} onToggle={(v) => toggle(c, v)} onEdit={() => { setEditing(c); setDialogOpen(true); }} onDelete={() => setToDelete(c)} />
            ))}
            {!filtered.length && <li className="py-6 text-center text-sm text-ink-muted">No committees found.</li>}
          </ul>
        </Panel>
        <BulkUpload onDone={load} />
      </div>
      <CommitteeDialog open={dialogOpen} onOpenChange={setDialogOpen} committee={editing} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Committees with votes can't be deleted — deactivate them instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-btn" className="bg-maroon">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
